import { ajouterJours, ajouterMois } from './calendrier'
import type { Frequence } from './domaine'
import { normaliserLibelle } from './libelle'

// Abonnements : les contrats de type « abonnement », classés par entité puis par banque, avec leur
// coût annuel et le dernier débit lu sur le relevé. Fonctions pures, sans base.

/** Nombre de prélèvements par an ; un achat ponctuel ne s'annualise pas. */
const PAR_AN: Record<Frequence, number> = {
  mensuelle: 12,
  bimestrielle: 6,
  trimestrielle: 4,
  semestrielle: 2,
  annuelle: 1,
  ponctuelle: 0,
}

/** Marge après la date attendue du débit suivant : un prélèvement glisse de quelques jours. */
const GRACE_JOURS = 15

/** Écart de montant admis quand plusieurs abonnements partagent un libellé de relevé. */
const ECART_MONTANT = 0.05

export function coutAnnuelCts(montantCts: number | null, frequence: Frequence): number | null {
  if (montantCts === null || PAR_AN[frequence] === 0) return null
  return montantCts * PAR_AN[frequence]
}

export type DebitReleve = {
  compteId: string
  date: string
  /** Positif : ce qui est sorti du compte. */
  montantCts: number
  libelleNormalise: string
}

export type DernierDebit = { date: string; montantCts: number }

type AbonnementSuivi = {
  id: string
  compteId: string | null
  motifReleve: string | null
  montantCts: number | null
}

/**
 * Dernier débit de chaque abonnement : même compte, libellé contenant le motif. Quand plusieurs
 * abonnements ont le même motif (un magasin d'applications), chaque débit va au montant le plus
 * proche, à condition d'en être très près : un achat au même libellé ne compte pour aucun.
 */
export function dernierDebitParAbonnement(
  abonnements: readonly AbonnementSuivi[],
  debits: readonly DebitReleve[],
): Map<string, DernierDebit> {
  const suivis = abonnements.flatMap((a) =>
    a.compteId && a.motifReleve
      ? [{ ...a, compteId: a.compteId, motif: normaliserLibelle(a.motifReleve) }]
      : [],
  )
  const resultat = new Map<string, DernierDebit>()
  for (const debit of debits) {
    const candidats = suivis.filter(
      (a) => a.compteId === debit.compteId && debit.libelleNormalise.includes(a.motif),
    )
    const elu = candidats.length > 1 ? parMontant(candidats, debit.montantCts) : candidats[0]
    if (!elu) continue
    const connu = resultat.get(elu.id)
    if (!connu || debit.date > connu.date) {
      resultat.set(elu.id, { date: debit.date, montantCts: debit.montantCts })
    }
  }
  return resultat
}

function parMontant<T extends { montantCts: number | null }>(
  candidats: readonly T[],
  montantCts: number,
): T | undefined {
  const ecart = (a: T) => Math.abs((a.montantCts ?? Infinity) - montantCts)
  const proche = candidats.reduce((m, a) => (ecart(a) < ecart(m) ? a : m))
  return ecart(proche) <= (proche.montantCts ?? 0) * ECART_MONTANT ? proche : undefined
}

export const ETATS_ABONNEMENT = ['a_jour', 'sans_debit_recent', 'non_suivi'] as const
export type EtatAbonnement = (typeof ETATS_ABONNEMENT)[number]

const MOIS_ENTRE_DEBITS: Record<Frequence, number> = {
  mensuelle: 1,
  bimestrielle: 2,
  trimestrielle: 3,
  semestrielle: 6,
  annuelle: 12,
  ponctuelle: 0,
}

/**
 * « Sans débit récent » n'est allumé que si le relevé du compte couvre la date où le débit était
 * attendu : un relevé en retard ne prouve rien (même règle que « en retard » pour un paiement).
 */
export function etatAbonnement(a: {
  compteId: string | null
  motifReleve: string | null
  frequence: Frequence
  dernierDebit: DernierDebit | null
  releveCouvertJusquAu: string | null
}): EtatAbonnement {
  if (!a.compteId || !a.motifReleve || !a.releveCouvertJusquAu) return 'non_suivi'
  if (a.frequence === 'ponctuelle') return 'non_suivi'
  if (!a.dernierDebit) return 'sans_debit_recent'
  const attendu = ajouterMois(a.dernierDebit.date, MOIS_ENTRE_DEBITS[a.frequence])
  return ajouterJours(attendu, GRACE_JOURS) < a.releveCouvertJusquAu
    ? 'sans_debit_recent'
    : 'a_jour'
}

export type Totaux = Record<string, number>

type LigneRegroupable = {
  entiteId: string
  entite: string
  banque: string | null
  coutAnnuelCts: number | null
  devise: string
}

export type BanqueAbonnements<L> = { banque: string | null; lignes: L[]; totaux: Totaux }
export type EntiteAbonnements<L> = {
  entiteId: string
  entite: string
  banques: BanqueAbonnements<L>[]
  totaux: Totaux
}

function totaliser(lignes: readonly LigneRegroupable[]): Totaux {
  const totaux: Totaux = {}
  for (const l of lignes) {
    if (l.coutAnnuelCts !== null) totaux[l.devise] = (totaux[l.devise] ?? 0) + l.coutAnnuelCts
  }
  return totaux
}

function grouper<L, K>(lignes: readonly L[], cle: (l: L) => K): Map<K, L[]> {
  const groupes = new Map<K, L[]>()
  for (const l of lignes) {
    const cleLigne = cle(l)
    const groupe = groupes.get(cleLigne)
    if (groupe) groupe.push(l)
    else groupes.set(cleLigne, [l])
  }
  return groupes
}

/** Par entité puis par banque (banque inconnue en dernier), le coût annuel le plus fort d'abord. */
export function regrouperAbonnements<L extends LigneRegroupable>(
  lignes: readonly L[],
): { entites: EntiteAbonnements<L>[]; totaux: Totaux } {
  const entites = [...grouper(lignes, (l) => l.entiteId).values()]
    .map((deLEntite) => ({
      entiteId: deLEntite[0]!.entiteId,
      entite: deLEntite[0]!.entite,
      totaux: totaliser(deLEntite),
      banques: [...grouper(deLEntite, (l) => l.banque).entries()]
        .map(([banque, deLaBanque]) => ({
          banque,
          totaux: totaliser(deLaBanque),
          lignes: [...deLaBanque].sort((a, b) => (b.coutAnnuelCts ?? -1) - (a.coutAnnuelCts ?? -1)),
        }))
        .sort((a, b) =>
          a.banque === null ? 1 : b.banque === null ? -1 : a.banque.localeCompare(b.banque, 'fr'),
        ),
    }))
    .sort((a, b) => a.entite.localeCompare(b.entite, 'fr'))
  return { entites, totaux: totaliser(lignes) }
}
